// App languages (fr / en / ar). The choice is kept per browser and also sets
// <html lang dir>, so Arabic switches the whole app to right-to-left.
// Messages live in ./locales; French is the reference and the fallback.
import { createI18n } from "vue-i18n";
import fr from "./locales/fr";
import en from "./locales/en";
import ar from "./locales/ar";

export const LOCALES = [
  { code: "fr", label: "Français", short: "FR", dir: "ltr", dateLocale: "fr-FR" },
  { code: "en", label: "English", short: "EN", dir: "ltr", dateLocale: "en-GB" },
  { code: "ar", label: "العربية", short: "ع", dir: "rtl", dateLocale: "ar-MA" },
] as const;

export type Locale = (typeof LOCALES)[number]["code"];

const STORAGE_KEY = "app_locale";
const DEFAULT_LOCALE: Locale = "fr";

function isLocale(value: unknown): value is Locale {
  return LOCALES.some((l) => l.code === value);
}

function savedLocale(): Locale {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (isLocale(value)) return value;
  } catch {
    // storage blocked: keep the default
  }
  return DEFAULT_LOCALE;
}

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: savedLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  messages: { fr, en, ar },
  missingWarn: false,
  fallbackWarn: false,
});

export function currentLocale(): Locale {
  return i18n.global.locale.value as Locale;
}

export function localeMeta(code: Locale = currentLocale()) {
  return LOCALES.find((l) => l.code === code) ?? LOCALES[0];
}

// Locale for Intl / toLocaleString (dates, numbers) in the current language
export function dateLocale(): string {
  return localeMeta().dateLocale;
}

function applyToDocument(code: Locale) {
  const html = document.documentElement;
  html.lang = code;
  html.dir = localeMeta(code).dir;
}

export function setLocale(code: Locale) {
  if (!isLocale(code)) return;
  i18n.global.locale.value = code;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // storage blocked: the choice lasts until reload
  }
  applyToDocument(code);
}

applyToDocument(currentLocale());

// Another tab changed the language: follow it
window.addEventListener("storage", (event) => {
  if (event.key === STORAGE_KEY && isLocale(event.newValue) && event.newValue !== currentLocale()) {
    i18n.global.locale.value = event.newValue;
    applyToDocument(event.newValue);
  }
});

export default i18n;
